/**
 * TEFAS API Client
 *
 * This module provides a simplified interface for accessing TEFAS API data.
 * It abstracts away HTTP implementation details and provides a clean, business-focused API.
 */

import { ApiClient } from './client';
import {
  ApiResponse,
  FundType,
  FundHistoryResponse,
  FundContentResponse,
  FundResponse,
  FundDetail,
  TefasApiRequest,
  TefasApiResponse,
  TefasAllocationApiResponse,
  TefasClientConfig,
  SearchResult,
  SearchOptions
} from './types';
import {
  InvalidFundTypeError,
  InvalidDateError,
  DateRangeError,
  FundNotFoundError,
  TefasApiError,
  NetworkError,
  ConfigurationError,
  ValidationError
} from './errors';
import { validateFundType, SUPPORTED_FUND_TYPES } from './utils/fund-type-validation';
import { convertToTefasDateFormat, addDays, getDaysBetween, validateDateRange, normalizeFuzzyDateRange } from './utils/date-validation';
import { mapTefasResponse } from './utils/response-mapping';
import { parseSearchResponse } from './utils/search-response-parser';
import { mapAllocationResponse } from './utils/allocation-response-mapping';
import { DEFAULT_MAX_DAYS_PER_REQUEST, TEFAS_ALLOCATION_ENDPOINT } from './constants';
import { CacheAdapter, CacheStats, isCacheAdapterWithStats } from './cache-interface';
import { MemoryCacheAdapter } from './adapters/memory-cache-adapter';

const FUNDFY_BASE_URL = 'https://api.fundfy.net';
const FUNDFY_AUTH_TOKEN = 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJFcm9sS09DQU1BTiIsImlhdCI6MTczMjYyNTQ2OH0.q2otvxUSxXdIO7zx0NLjN5oRhreR_GReFKRRYPsOXcs';
const FUNDFY_ORIGIN = 'https://app.fundfy.net';

// Ambient declaration for build-time injected constant
declare const __VERSION__: string;

/**
 * Simplified TEFAS API client that provides a clean interface for fund data access.
 *
 * This client wraps the base REST API client with TEFAS-specific configuration
 * and methods, hiding HTTP implementation details from users.
 *
 * @example
 * ```typescript
 * import { TefasClient, FundType } from 'tefas-client';
 *
 * const client = new TefasClient();
 *
 * // Get all pension funds for a date range
 * const funds = await client.getFundHistory(
 *   '2024-01-01',
 *   '2024-12-31',
 *   undefined,
 *   FundType.EMK
 * );
 *
 * // Get all funds across all types
 * const allFunds = await client.getFundHistory(
 *   '2024-01-01',
 *   '2024-12-31'
 * );
 *
 * // Get specific fund history
 * const specificFund = await client.getFundHistory(
 *   '2024-01-01',
 *   '2024-12-31',
 *   'TGE',
 *   FundType.EMK
 * );
 * ```
 */
export class TefasClient {
  private readonly baseClient: ApiClient;
  private readonly config: TefasClientConfig;
  private maxDaysPerRequest: number = DEFAULT_MAX_DAYS_PER_REQUEST;
  private readonly cache: CacheAdapter<ApiResponse<FundResponse>>;

  /**
   * Creates a new TEFAS client instance.
   *
   * No configuration is required - the client uses hardcoded TEFAS API settings
   * for zero-configuration setup. Optionally accepts a partial configuration
   * to override default settings like cache behavior.
   *
   * @param configOverrides - Optional configuration overrides
   * @throws ConfigurationError if TEFAS API configuration is invalid
   */
  constructor(configOverrides?: Partial<TefasClientConfig>) {
    // Hardcoded TEFAS API configuration with optional overrides
    this.config = {
      baseUrl: 'https://www.tefas.gov.tr',
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'User-Agent': `TefasClient/${this.getVersion()}`
      },
      maxRetries: 3,
      retryDelay: 1000,
      cache: {
        enabled: true,
        ttl: 15 * 60 * 1000, // 15 minutes
        maxSize: 1000
      },
      ...configOverrides
    };

    // Initialize cache with configuration
    // If user provides a custom cache adapter, use it; otherwise use default memory cache
    this.cache = configOverrides?.cacheAdapter ||
      new MemoryCacheAdapter<ApiResponse<FundResponse>>({
        enabled: this.config.cache?.enabled ?? true,
        ttl: this.config.cache?.ttl ?? 15 * 60 * 1000,
        maxSize: this.config.cache?.maxSize ?? 1000
      });

    // Create base API client with TEFAS configuration
    this.baseClient = new ApiClient({
      baseUrl: this.config.baseUrl,
      timeout: this.config.timeout,
      headers: this.config.headers
    });

    // Validate configuration
    this.validateConfiguration();
  }

  /**
   * Returns library version string for User-Agent; falls back to 'dev' in tests.
   */
  private getVersion(): string {
    try {
      // __VERSION__ is injected at build; guard for test/runtime
      return typeof __VERSION__ !== 'undefined' ? __VERSION__ : 'dev';
    } catch {
      return 'dev';
    }
  }

  /**
   * Sets the maximum number of days per request window when fetching history.
   * Defaults to 90; values must be integers between 1 and 365.
   */
  public setMaxDaysPerRequest(maxDays: number): void {
    if (typeof maxDays !== 'number' || !Number.isInteger(maxDays)) {
      throw new ValidationError('maxDaysPerRequest must be an integer');
    }
    if (maxDays <= 0 || maxDays > 365) {
      throw new ValidationError('maxDaysPerRequest must be between 1 and 365');
    }
    this.maxDaysPerRequest = maxDays;
  }

  /**
   * Get fund history data for funds of a specific type or all types.
   *
   * @private
   * Internal method used by getFund. Use getFund() for public API access.
   *
   * This method retrieves historical fund data from TEFAS API. It supports both
   * standard date formats (YYYY-MM-DD) and fuzzy date parsing for natural language
   * expressions. The method automatically handles large date ranges by splitting
   * them into smaller chunks to comply with TEFAS API limitations.
   *
   * @param startDate - Start date in YYYY-MM-DD format or fuzzy date (e.g., "today", "yesterday", "2024-01-01")
   * @param endDate - End date in YYYY-MM-DD format or fuzzy date (e.g., "today", "yesterday", "2024-01-01")
   * @param fundCode - Optional specific fund code to retrieve (3-character code like "TGE", "IIH")
   * @param fundType - Optional fund type to filter by. If omitted, fetches all fund types
   * @returns Promise resolving to array of fund history data
   *
   * @throws InvalidFundTypeError if fund type is not supported
   * @throws InvalidDateError if date format is invalid
   * @throws FundNotFoundError if fund code is not found
   * @throws TefasApiError if TEFAS API returns an error
   * @throws NetworkError if network request fails
   *
   * @see {@link getFund} for public API access
   * @see {@link FundType} for available fund types
   * @see {@link FundHistoryResponse} for response structure
   */
  private async getFundHistory(
    startDate: string,
    endDate: string,
    fundCode?: string,
    fundType?: FundType
  ): Promise<ApiResponse<FundHistoryResponse>> {
    try {
      // Normalize fuzzy dates to YYYY-MM-DD format
      const { startDate: normalizedStartDate, endDate: normalizedEndDate } =
        normalizeFuzzyDateRange(startDate, endDate);

      // Validate input parameters with normalized dates
      this.validateFundHistoryRequest(fundType, normalizedStartDate, normalizedEndDate, fundCode);

      // If fundType is provided, fetch only that type
      if (fundType) {
        return this.fetchFundHistoryForType(fundType, normalizedStartDate, normalizedEndDate, fundCode);
      }

      // If no fundType provided, fetch all fund types and merge results
      return this.fetchAllFundTypes(normalizedStartDate, normalizedEndDate, fundCode);
    } catch (error) {
      // Translate errors to business-focused messages
      throw this.translateError(error);
    }
  }

  /**
   * Search for funds by name or code prefix
   *
   * Enables autocomplete and fund selection by searching for funds matching
   * the provided query string. The search is performed against both fund codes
   * and fund names. Optionally filter by fund type and limit the number of results.
   *
   * This method is perfect for implementing autocomplete functionality, fund
   * selection dropdowns, or finding funds by partial name matches. The search
   * is case-insensitive and supports Turkish characters.
   *
   * @param query - Search query (fund code or name prefix)
   * @param options - Optional search configuration (fundType filter, result limit)
   * @returns Promise resolving to array of matching funds
   *
   * @throws ValidationError if query is empty or limit is invalid
   * @throws InvalidFundTypeError if fundType is invalid
   * @throws TefasApiError if TEFAS API returns an error
   * @throws NetworkError if network request fails
   *
   * @example
   * ```typescript
   * import { TefasClient, FundType } from '@firstthumb/tefas-api';
   *
   * const client = new TefasClient();
   *
   * // Basic search by code prefix
   * const results = await client.searchFund('IIH');
   * console.log(results);
   * // [{ fundCode: "IIH", fundName: "IIH - İSTANBUL PORTFÖY..." }]
   *
   * // Search with fund type filter
   * const yatFunds = await client.searchFund('IS', { fundType: FundType.YAT });
   * console.log(yatFunds); // Only investment funds (YAT type)
   *
   * // Search with custom result limit (great for autocomplete)
   * const limitedResults = await client.searchFund('A', { limit: 5 });
   * console.log(limitedResults.length); // At most 5 results
   *
   * // Combine filters and limits
   * const filtered = await client.searchFund('IS', {
   *   fundType: FundType.EMK,
   *   limit: 3
   * });
   *
   * // Use fund code for detailed history
   * if (results.length > 0) {
   *   const history = await client.getFundHistory(
   *     '2024-01-01',
   *     '2024-12-31',
   *     results[0].fundCode,
   *     FundType.YAT
   *   );
   * }
   *
   * // Autocomplete implementation example
   * async function autocompleteFunds(query: string) {
   *   if (query.length < 2) return [];
   *
   *   try {
   *     const results = await client.searchFund(query, { limit: 10 });
   *     return results.map(fund => ({
   *       value: fund.fundCode,
   *       label: fund.fundName,
   *       code: fund.fundCode
   *     }));
   *   } catch (error) {
   *     console.error('Search failed:', error);
   *     return [];
   *   }
   * }
   * ```
   *
   * @since 1.0.0
   * @see {@link SearchResult} for result structure
   * @see {@link SearchOptions} for configuration options
   */
  /**
   * Search funds for a specific fund type
   *
   * @private
   * @param query - Search query string
   * @param fundType - Fund type to search
   * @param limit - Optional limit for number of results
   * @returns Array of SearchResult objects for the specified fund type
   */
  private async searchFundByType(
    query: string,
    fundType: FundType,
    limit?: number
  ): Promise<SearchResult[]> {
    // Build search API request
    const searchRequest = {
      prefixText: query.trim(),
      count: limit ?? 10,
      contextKey: fundType
    };

    // Make API call to GetAllFunds endpoint
    const response = await this.baseClient.post(
      '/Service.asmx/GetAllFunds',
      searchRequest,
      { contentType: 'json' }
    );

    // Parse nested JSON response with fundType
    return parseSearchResponse(response, fundType);
  }

  async searchFund(query: string, options?: SearchOptions): Promise<ApiResponse<SearchResult>> {
    try {
      // Validate input
      this.validateSearchQuery(query);

      // Validate options if provided
      if (options?.fundType) {
        validateFundType(options.fundType);
      }

      if (options?.limit !== undefined) {
        this.validateLimit(options.limit);
      }

      const trimmedQuery = query.trim();
      const limit = options?.limit;

      // If fundType is provided, search only that type
      if (options?.fundType) {
        const results = await this.searchFundByType(trimmedQuery, options.fundType, limit);
        return { results };
      }

      // If no fundType filter, search all fund types in parallel
      // Use a higher limit per type to ensure we have enough results after merging
      // If limit is provided, use at least that amount per type, otherwise use default 10
      const perTypeLimit = limit ? Math.max(limit, 10) : undefined;

      const searchPromises = SUPPORTED_FUND_TYPES.map(fundType =>
        this.searchFundByType(trimmedQuery, fundType, perTypeLimit).catch((error) => {
          // Log error but continue with other searches
          // Return empty array for failed searches
          console.warn(`Search failed for fund type ${fundType}:`, error);
          return [] as SearchResult[];
        })
      );

      // Wait for all searches to complete
      const allResults = await Promise.all(searchPromises);

      // Merge and deduplicate results by fundCode
      const resultsMap = new Map<string, SearchResult>();
      for (const fundTypeResults of allResults) {
        for (const result of fundTypeResults) {
          // Only add if we haven't seen this fundCode before
          if (!resultsMap.has(result.fundCode)) {
            resultsMap.set(result.fundCode, result);
          }
        }
      }

      // Convert to array and apply limit if provided
      let results = Array.from(resultsMap.values());
      if (limit !== undefined && limit > 0) {
        results = results.slice(0, limit);
      }

      return { results };
    } catch (error) {
      // Translate errors to business-focused messages
      throw this.translateError(error);
    }
  }

  /**
   * Fetch fund detail data from Fundfy and return selected fields.
   *
   * @private
   * @param fundCode - Fund code identifier
   * @returns Fund detail information
   */
  private async getFundDetails(fundCode: string): Promise<FundDetail> {
    try {
      if (!fundCode || typeof fundCode !== 'string' || fundCode.trim().length === 0) {
        throw new ValidationError('fundCode must be a non-empty string');
      }

      const normalizedCode = fundCode.trim().toUpperCase();
      const fundfyClient = this.baseClient.withConfig({
        baseUrl: FUNDFY_BASE_URL,
        headers: {
          ...this.config.headers,
          Origin: FUNDFY_ORIGIN,
          Referer: `${FUNDFY_ORIGIN}/`,
          Authorization: FUNDFY_AUTH_TOKEN,
          Accept: 'application/json'
        }
      });

      const response = await fundfyClient.get<FundfyDetailResponse>(
        `/api/v1/fund/detail/${encodeURIComponent(normalizedCode)}`
      );

      if (!response?.fund) {
        throw new FundNotFoundError(normalizedCode);
      }

      return this.mapFundfyResponse(response.fund);
    } catch (error) {
      throw this.translateError(error);
    }
  }

  /**
   * Validates limit parameter
   *
   * @private
   * @param limit - Limit value to validate
   * @throws ValidationError if limit is invalid
   */
  private validateLimit(limit: number): void {
    if (typeof limit !== 'number') {
      throw new ValidationError('limit must be a number');
    }

    if (!Number.isInteger(limit)) {
      throw new ValidationError('limit must be an integer');
    }

    if (limit <= 0) {
      throw new ValidationError('limit must be a positive integer');
    }
  }

  /**
   * Validates search query parameter
   *
   * @private
   * @param query - Search query to validate
   * @throws ValidationError if query is invalid
   */
  private validateSearchQuery(query: string): void {
    // Check if query is provided
    if (!query || typeof query !== 'string') {
      throw new ValidationError('query must be a non-empty string');
    }

    // Check if query is not just whitespace
    if (query.trim().length === 0) {
      throw new ValidationError('query must be a non-empty string');
    }
  }

  /**
   * Get fund allocation/portfolio composition data for funds of a specific type or all types.
   *
   * @private
   * Internal method used by getFund. Use getFund() for public API access.
   *
   * This method retrieves historical fund allocation data from TEFAS API. It supports both
   * standard date formats (YYYY-MM-DD) and fuzzy date parsing for natural language
   * expressions. The method automatically handles large date ranges by splitting
   * them into smaller chunks to comply with TEFAS API limitations.
   *
   * @param startDate - Start date in YYYY-MM-DD format or fuzzy date (e.g., "today", "yesterday", "2024-01-01")
   * @param endDate - End date in YYYY-MM-DD format or fuzzy date (e.g., "today", "yesterday", "2024-01-01")
   * @param fundCode - Optional specific fund code to retrieve (3-character code like "TGE", "IIH")
   * @param fundType - Optional fund type to filter by. If omitted, fetches all fund types
   * @returns Promise resolving to array of fund allocation data
   *
   * @throws InvalidFundTypeError if fund type is not supported
   * @throws InvalidDateError if date format is invalid
   * @throws FundNotFoundError if fund code is not found
   * @throws TefasApiError if TEFAS API returns an error
   * @throws NetworkError if network request fails
   *
   * @see {@link getFund} for public API access
   * @see {@link FundType} for available fund types
   * @see {@link FundContentResponse} for response structure
   */
  private async getFundContent(
    startDate: string,
    endDate: string,
    fundCode?: string,
    fundType?: FundType
  ): Promise<ApiResponse<FundContentResponse>> {
    try {
      // Normalize fuzzy dates to YYYY-MM-DD format
      const { startDate: normalizedStartDate, endDate: normalizedEndDate } =
        normalizeFuzzyDateRange(startDate, endDate);

      // Validate input parameters with normalized dates
      this.validateFundContentRequest(fundType, normalizedStartDate, normalizedEndDate, fundCode);

      // If fundType is provided, fetch only that type
      if (fundType) {
        return this.fetchFundContentForType(fundType, normalizedStartDate, normalizedEndDate, fundCode);
      }

      // If no fundType provided, fetch all fund types and merge results
      return this.fetchAllFundTypesContent(normalizedStartDate, normalizedEndDate, fundCode);
    } catch (error) {
      // Translate errors to business-focused messages
      throw this.translateError(error);
    }
  }

  /**
   * Get merged fund data combining history and content information.
   *
   * This method retrieves both fund history and allocation data from TEFAS API
   * and merges them based on fund code and date. It provides a unified view of
   * fund data where fields from either source may be null if the corresponding
   * data is not available for that fund/date combination.
   *
   * **Filtering Behavior:**
   * - When `fundType` is provided: Searches only that specific fund type
   * - When `fundType` is omitted: Searches across ALL fund types (EMK, YAT, BYF, GYF, GSYF)
   * - When both `fundCode` and `fundType` are provided: Filters to that specific fund in that type
   *
   * This is the primary public API for fetching fund data. For searching funds by name/code, use {@link searchFund}.
   *
   * The method supports both standard date formats (YYYY-MM-DD) and fuzzy date
   * parsing for natural language expressions. It automatically handles large
   * date ranges by splitting them into smaller chunks to comply with TEFAS API
   * limitations.
   *
   * @param startDate - Start date in YYYY-MM-DD format or fuzzy date (e.g., "today", "yesterday", "2024-01-01")
   * @param endDate - End date in YYYY-MM-DD format or fuzzy date (e.g., "today", "yesterday", "2024-01-01")
   * @param fundCode - Optional specific fund code to retrieve (3-character code like "TGE", "IIH")
   * @param fundType - Optional fund type to filter by (EMK, YAT, BYF, GYF, GSYF). If omitted, searches all types.
   * @returns Promise resolving to array of merged fund data
   *
   * @throws InvalidFundTypeError if fund type is invalid
   * @throws InvalidDateError if date format is invalid
   * @throws FundNotFoundError if fund code is not found
   * @throws TefasApiError if TEFAS API returns an error
   * @throws NetworkError if network request fails
   *
   * @example
   * ```typescript
   * import { TefasClient, FundType } from '@firstthumb/tefas-api';
   *
   * const client = new TefasClient();
   *
   * // Get merged data for a specific fund using fuzzy dates
   * const fundData = await client.getFund(
   *   'yesterday',
   *   'today',
   *   'TGE'
   * );
   * console.log(fundData[0].price); // Fund price
   * console.log(fundData[0].stocks); // Stock allocation percentage
   *
   * // Get merged data for all funds across all types
   * const allFunds = await client.getFund(
   *   '2024-01-01',
   *   '2024-12-31'
   * );
   * console.log(allFunds.length); // Total number of fund/date combinations
   *
   * // Filter by fund type - get only pension funds (EMK)
   * const pensionFunds = await client.getFund(
   *   '2024-01-01',
   *   '2024-12-31',
   *   undefined,
   *   FundType.EMK
   * );
   *
   * // Get specific fund in specific type
   * const specificPensionFund = await client.getFund(
   *   'last week',
   *   'today',
   *   'TGE',
   *   FundType.EMK
   * );
   *
   * // Handle missing data gracefully
   * fundData.forEach(fund => {
   *   if (fund.price !== null) {
   *     console.log(`Price: ${fund.price}`);
   *   }
   *   if (fund.stocks !== null && fund.stocks !== undefined) {
   *     console.log(`Stock allocation: ${fund.stocks}%`);
   *   }
   * });
   *
   * // Handle errors gracefully
   * try {
   *   const data = await client.getFund('2024-01-01', '2024-01-31', 'INVALID');
   * } catch (error) {
   *   if (error instanceof FundNotFoundError) {
   *     console.log('Fund not found');
   *   }
   * }
   * ```
   *
   * @since 1.0.0
   * @see {@link FundResponse} for response structure
   * @see {@link getFundHistory} for history-only data
   * @see {@link getFundContent} for content-only data
   */
  async getFund(
    startDate: string,
    endDate: string,
    fundCode?: string,
    fundType?: FundType,
    skipFundDetails: boolean = false
  ): Promise<ApiResponse<FundResponse>> {
    try {
      // Validate fund type if provided
      if (fundType) {
        validateFundType(fundType);
      }

      // Normalize fuzzy dates to YYYY-MM-DD format
      const { startDate: normalizedStartDate, endDate: normalizedEndDate } =
        normalizeFuzzyDateRange(startDate, endDate);

      // Auto-detect fundType if fundCode is provided but fundType is not
      let resolvedFundType = fundType;
      if (fundCode && !resolvedFundType) {
        const searchResults = await this.searchFund(fundCode, { limit: 10 });
        if (searchResults.results.length === 0) {
          throw new FundNotFoundError(fundCode);
        }
        // Find exact match by fundCode (case-insensitive)
        const matchedFund = searchResults.results.find(
          r => r.fundCode.toUpperCase() === fundCode.trim().toUpperCase()
        );
        if (!matchedFund) {
          throw new FundNotFoundError(fundCode);
        }
        resolvedFundType = matchedFund.fundType;
      }

      // Generate cache key using resolved fundType
      const cacheKey = this.generateCacheKey(
        normalizedStartDate,
        normalizedEndDate,
        fundCode,
        resolvedFundType
      );

      // Check cache first
      const cachedResult = await this.cache.get(cacheKey);
      if (cachedResult) {
        return cachedResult;
      }

      // Fetch history and content data sequentially to avoid rate limiting
      const historyResponse = await this.getFundHistory(normalizedStartDate, normalizedEndDate, fundCode, resolvedFundType);
      const contentResponse = await this.getFundContent(normalizedStartDate, normalizedEndDate, fundCode, resolvedFundType);

      // Extract data from wrapped responses
      const historyData = historyResponse.results;
      const contentData = contentResponse.results;

      // Extract unique fund codes from history and content data
      const uniqueFundCodes = new Set<string>();
      for (const history of historyData) {
        uniqueFundCodes.add(history.fundCode);
      }
      for (const content of contentData) {
        uniqueFundCodes.add(content.fundCode);
      }

      // Fetch fund details for each unique fund code (unless skipped for performance)
      const fundDetailsMap = new Map<string, FundDetail | null>();
      if (!skipFundDetails) {
        const totalFundCodes = uniqueFundCodes.size;
        if (totalFundCodes > 0) {
          console.log(`Fetching fund details for ${totalFundCodes} unique fund codes...`);
        }
        let detailsFetched = 0;
        for (const code of uniqueFundCodes) {
          try {
            const details = await this.getFundDetails(code);
            fundDetailsMap.set(code, details);
            detailsFetched++;
            if (detailsFetched % 50 === 0) {
              console.log(`Fetched fund details: ${detailsFetched}/${totalFundCodes}...`);
            }
          } catch {
            // If getFundDetails fails, set null - details unavailable for this fund
            fundDetailsMap.set(code, null);
          }
        }
        if (totalFundCodes > 0) {
          console.log(`Completed fetching fund details: ${detailsFetched}/${totalFundCodes} successful`);
        }
      } else {
        // Skip fund details - set all to null for faster bulk operations
        for (const code of uniqueFundCodes) {
          fundDetailsMap.set(code, null);
        }
        console.log(`Skipping fund details fetch for ${uniqueFundCodes.size} funds (skipFundDetails=true)`);
      }

      // Create maps for efficient lookup by fundCode + date
      const historyMap = new Map<string, FundHistoryResponse>();
      const contentMap = new Map<string, FundContentResponse>();

      // Populate history map
      for (const history of historyData) {
        const key = `${history.fundCode}_${history.date}`;
        historyMap.set(key, history);
      }

      // Populate content map
      for (const content of contentData) {
        const key = `${content.fundCode}_${content.date}`;
        contentMap.set(key, content);
      }

      // Collect all unique fundCode + date combinations
      const allKeys = new Set<string>();
      for (const key of historyMap.keys()) {
        allKeys.add(key);
      }
      for (const key of contentMap.keys()) {
        allKeys.add(key);
      }

      // Merge data for each unique combination
      const mergedData: FundResponse[] = [];
      for (const key of allKeys) {
        const [fundCode, date] = key.split('_');
        const history = historyMap.get(key);
        const content = contentMap.get(key);

        // Determine fund name and type from available data
        const fundName = history?.fundName || content?.fundName || '';
        const fundType = history?.fundType || content?.fundType || FundType.EMK;

        // Get fund details for this fund code
        const details = fundDetailsMap.get(fundCode);

        // Create merged response
        const merged: FundResponse = {
          fundCode,
          fundName,
          fundType,
          date,

          // History fields
          price: history?.price ?? null,
          volume: history?.volume ?? null,
          assets: history?.assets ?? null,
          investorCount: history?.investorCount ?? null,

          // Content fields
          totalValue: content?.totalValue ?? null,
          stocks: content?.stocks ?? null,
          foreignSecurities: content?.foreignSecurities ?? null,
          stockBasedFunds: content?.stockBasedFunds ?? null,
          stockBasedInvestmentFunds: content?.stockBasedInvestmentFunds ?? null,
          bonds: content?.bonds ?? null,
          governmentBonds: content?.governmentBonds ?? null,
          privateSectorBonds: content?.privateSectorBonds ?? null,
          governmentSecurities: content?.governmentSecurities ?? null,
          governmentSecuritiesYield: content?.governmentSecuritiesYield ?? null,
          governmentYieldBonds: content?.governmentYieldBonds ?? null,
          governmentYield: content?.governmentYield ?? null,
          deposits: content?.deposits ?? null,
          reversePurchaseAgreements: content?.reversePurchaseAgreements ?? null,
          preciousMetals: content?.preciousMetals ?? null,
          cashAndCashEquivalents: content?.cashAndCashEquivalents ?? null,
          otherSecurities: content?.otherSecurities ?? null,
          otherSecuritiesYield: content?.otherSecuritiesYield ?? null,
          treasuryBills: content?.treasuryBills ?? null,
          individualInvestmentFunds: content?.individualInvestmentFunds ?? null,
          governmentDebtInstruments: content?.governmentDebtInstruments ?? null,
          governmentDebtInstrumentsForeign: content?.governmentDebtInstrumentsForeign ?? null,
          eurobonds: content?.eurobonds ?? null,
          foreignBonds: content?.foreignBonds ?? null,
          foreignCurrencyBonds: content?.foreignCurrencyBonds ?? null,
          goldAndSilver: content?.goldAndSilver ?? null,
          participationAccounts: content?.participationAccounts ?? null,
          participationAccountsGold: content?.participationAccountsGold ?? null,
          participationAccountsGoldForeign: content?.participationAccountsGoldForeign ?? null,
          participationAccountsForeignCurrency: content?.participationAccountsForeignCurrency ?? null,
          participationAccountsTurkishLira: content?.participationAccountsTurkishLira ?? null,
          publicLeaseCertificates: content?.publicLeaseCertificates ?? null,
          publicLeaseCertificatesForeignCurrency: content?.publicLeaseCertificatesForeignCurrency ?? null,
          publicLeaseCertificatesTurkishLira: content?.publicLeaseCertificatesTurkishLira ?? null,
          publicLeaseCertificatesForeign: content?.publicLeaseCertificatesForeign ?? null,
          participationAccountsForeignCurrencyBonds: content?.participationAccountsForeignCurrencyBonds ?? null,
          otherSecuritiesTurkishLira: content?.otherSecuritiesTurkishLira ?? null,
          repoTransactions: content?.repoTransactions ?? null,
          termDeposits: content?.termDeposits ?? null,
          treasuryBillsShort: content?.treasuryBillsShort ?? null,
          variableRateDeposits: content?.variableRateDeposits ?? null,
          moneyMarketInstruments: content?.moneyMarketInstruments ?? null,
          moneyMarketInstrumentsGold: content?.moneyMarketInstrumentsGold ?? null,
          moneyMarketInstrumentsForeignCurrency: content?.moneyMarketInstrumentsForeignCurrency ?? null,
          moneyMarketInstrumentsTurkishLira: content?.moneyMarketInstrumentsTurkishLira ?? null,
          variableIncomeInstruments: content?.variableIncomeInstruments ?? null,
          investmentFundParticipations: content?.investmentFundParticipations ?? null,
          investmentFundParticipationsBonds: content?.investmentFundParticipationsBonds ?? null,
          investmentFundParticipationsOtherSecurities: content?.investmentFundParticipationsOtherSecurities ?? null,
          investmentFundParticipationsForeignCurrency: content?.investmentFundParticipationsForeignCurrency ?? null,
          investmentFundParticipationsMetals: content?.investmentFundParticipationsMetals ?? null,
          preciousMetalsInvestmentFunds: content?.preciousMetalsInvestmentFunds ?? null,
          preciousMetalsGovernmentDebtInstruments: content?.preciousMetalsGovernmentDebtInstruments ?? null,
          preciousMetalsPublicLeaseCertificates: content?.preciousMetalsPublicLeaseCertificates ?? null,

          // Fund detail fields
          auditFirm: details?.auditFirm ?? null,
          emailAddress: details?.emailAddress ?? null,
          fundDuration: details?.fundDuration ?? null,
          contactAddress: details?.contactAddress ?? null,
          founder: details?.founder ?? null,
          founderLogo: details?.founderLogo ?? null,
          umbrellaFundType: details?.umbrellaFundType ?? null,
          umbrellaFundName: details?.umbrellaFundName ?? null,
          fundCategory: details?.fundCategory ?? null,
          risk: details?.risk ?? null,
          yearlyManagementFee: details?.yearlyManagementFee ?? null,
          buyingValor: details?.buyingValor ?? null,
          sellingValor: details?.sellingValor ?? null,
          strategyStatement: details?.strategyStatement ?? null,
          fundBenchmarks: details?.fundBenchmarks ?? []
        };

        mergedData.push(merged);
      }

      // Sort by date ascending for consistent ordering
      mergedData.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

      const result = { results: mergedData };

      // Store result in cache
      await this.cache.set(cacheKey, result);

      return result;
    } catch (error) {
      // Translate errors to business-focused messages
      throw this.translateError(error);
    }
  }

  /**
   * Validates the TEFAS API configuration.
   *
   * @private
   * @throws ConfigurationError if configuration is invalid
   */
  private validateConfiguration(): void {
    if (!this.config.baseUrl || typeof this.config.baseUrl !== 'string') {
      throw new ConfigurationError('Invalid base URL configuration');
    }

    if (this.config.timeout <= 0) {
      throw new ConfigurationError('Invalid timeout configuration');
    }

    if (!this.config.headers || typeof this.config.headers !== 'object') {
      throw new ConfigurationError('Invalid headers configuration');
    }

    // Validate required headers
    const requiredHeaders = ['Content-Type', 'Accept'];
    for (const header of requiredHeaders) {
      if (!this.config.headers[header]) {
        throw new ConfigurationError(`Missing required header: ${header}`);
      }
    }
  }

  /**
   * Validates fund history request parameters.
   *
   * @private
   * @param fundType - Fund type to validate (optional)
   * @param startDate - Start date to validate
   * @param endDate - End date to validate
   * @param fundCode - Optional fund code to validate
   * @throws InvalidFundTypeError if fund type is invalid
   * @throws InvalidDateError if date format is invalid
   * @throws DateRangeError if date range is invalid
   */
  private validateFundHistoryRequest(
    fundType: FundType | undefined,
    startDate: string,
    endDate: string,
    fundCode?: string
  ): void {
    // Validate fund type if provided
    if (fundType) {
      validateFundType(fundType);
    }

    // Validate date range (includes individual date validation)
    validateDateRange(startDate, endDate);

    // Validate fund code if provided
    if (fundCode !== undefined) {
      if (!fundCode || typeof fundCode !== 'string' || fundCode.trim().length === 0) {
        throw new InvalidDateError(fundCode, 'Fund code must be a non-empty string');
      }

      // TEFAS fund codes are typically 3 characters
      if (fundCode.trim().length !== 3) {
        throw new InvalidDateError(fundCode, 'Fund code must be exactly 3 characters');
      }
    }
  }

  /**
   * Validates fund content request parameters.
   *
   * @private
   * @param fundType - Fund type to validate (optional)
   * @param startDate - Start date to validate
   * @param endDate - End date to validate
   * @param fundCode - Optional fund code to validate
   * @throws InvalidFundTypeError if fund type is invalid
   * @throws InvalidDateError if date format is invalid
   * @throws DateRangeError if date range is invalid
   */
  private validateFundContentRequest(
    fundType: FundType | undefined,
    startDate: string,
    endDate: string,
    fundCode?: string
  ): void {
    // Validate fund type if provided
    if (fundType) {
      validateFundType(fundType);
    }

    // Validate date range (includes individual date validation)
    validateDateRange(startDate, endDate);

    // Validate fund code if provided
    if (fundCode !== undefined) {
      if (!fundCode || typeof fundCode !== 'string' || fundCode.trim().length === 0) {
        throw new InvalidDateError(fundCode, 'Fund code must be a non-empty string');
      }

      // TEFAS fund codes are typically 3 characters
      if (fundCode.trim().length !== 3) {
        throw new InvalidDateError(fundCode, 'Fund code must be exactly 3 characters');
      }
    }
  }

  /**
   * Fetches fund history for a specific fund type.
   *
   * @private
   * @param fundType - Fund type to fetch
   * @param startDate - Start date
   * @param endDate - End date
   * @param fundCode - Optional fund code
   * @param throwOnNotFound - Whether to throw error if fund not found (default: true)
   * @returns Promise resolving to fund history data
   */
  private async fetchFundHistoryForType(
    fundType: FundType,
    startDate: string,
    endDate: string,
    fundCode?: string,
    throwOnNotFound: boolean = true
  ): Promise<ApiResponse<FundHistoryResponse>> {
    // If the requested range exceeds maxDaysPerRequest, split into chunks
    const totalDays = getDaysBetween(startDate, endDate);
    const MAX_DAYS = this.maxDaysPerRequest;

    let fundHistory: FundHistoryResponse[] = [];
    if (totalDays > MAX_DAYS) {
      // Calculate all chunks
      const chunks: Array<{ start: string; end: string }> = [];
      let chunkStart = startDate;
      while (true) {
        const tentativeEnd = addDays(chunkStart, MAX_DAYS - 1);
        const chunkEnd = tentativeEnd > endDate ? endDate : tentativeEnd;
        chunks.push({ start: chunkStart, end: chunkEnd });
        if (chunkEnd === endDate) break;
        chunkStart = addDays(chunkEnd, 1);
      }

      console.log(`Fetching fund history [${fundType}] [fund: ${fundCode || 'all'}]: ${chunks.length} chunks (${totalDays} days total)`);

      // Execute chunks sequentially to avoid rate limiting
      for (let i = 0; i < chunks.length; i++) {
        const { start, end } = chunks[i];
        const chunkNumber = i + 1;
        const chunkName = `chunk ${chunkNumber}/${chunks.length} (${start} to ${end})`;

        console.log(`Fetching fund history [${fundType}] [fund: ${fundCode || 'all'}]: ${chunkName}`);

        const chunkHistory = await this.retryWithDelay(
          async () => {
            const chunkRequest = this.buildTefasRequest(fundType, start, end, fundCode);
            const chunkResponse = await this.baseClient.post<TefasApiResponse>(
              '/api/DB/BindHistoryInfo',
              chunkRequest,
              { contentType: 'form' }
            );
            return this.processTefasResponse(chunkResponse, fundType);
          },
          chunkName
        );

        fundHistory.push(...chunkHistory);
      }

      console.log(`Successfully fetched fund history [${fundType}] [fund: ${fundCode || 'all'}]: ${chunks.length}/${chunks.length} chunks (${totalDays} days)`);
    } else {
      // Single call path
      console.log(`Fetching fund history [${fundType}] [fund: ${fundCode || 'all'}]: single call (${startDate} to ${endDate})`);
      const tefasRequest = this.buildTefasRequest(fundType, startDate, endDate, fundCode);
      const response = await this.baseClient.post<TefasApiResponse>('/api/DB/BindHistoryInfo', tefasRequest, {
        contentType: 'form'
      });
      fundHistory = this.processTefasResponse(response, fundType);
      console.log(`Successfully fetched fund history [${fundType}] [fund: ${fundCode || 'all'}]: ${fundHistory.length} records`);
    }

    // Ensure deterministic ordering by date ascending
    fundHistory.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    // If specific fund code was requested, validate it exists
    if (fundCode && fundHistory.length === 0 && throwOnNotFound) {
      throw new FundNotFoundError(fundCode);
    }

    return { results: fundHistory };
  }

  /**
   * Fetches fund history for all fund types and merges results.
   *
   * @private
   * @param startDate - Start date
   * @param endDate - End date
   * @param fundCode - Optional fund code
   * @returns Promise resolving to merged fund history data
   */
  private async fetchAllFundTypes(
    startDate: string,
    endDate: string,
    fundCode?: string
  ): Promise<ApiResponse<FundHistoryResponse>> {
    const allFundTypes = SUPPORTED_FUND_TYPES;
    const allResults: FundHistoryResponse[] = [];

    // Fetch data for each fund type sequentially to avoid rate limiting
    for (const fundType of allFundTypes) {
      try {
        const response = await this.fetchFundHistoryForType(fundType, startDate, endDate, fundCode, false);
        allResults.push(...response.results);
      } catch {
        // If a specific fund type fails, continue with others
        continue;
      }
    }

    // If specific fund code was requested, validate it exists in at least one fund type
    if (fundCode && allResults.length === 0) {
      throw new FundNotFoundError(`Fund '${fundCode}' not found in any fund type`);
    }

    return { results: allResults };
  }

  /**
   * Fetches fund allocation data for a specific fund type.
   *
   * @private
   * @param fundType - Fund type to fetch
   * @param startDate - Start date
   * @param endDate - End date
   * @param fundCode - Optional fund code
   * @param throwOnNotFound - Whether to throw error if fund not found (default: true)
   * @returns Promise resolving to fund allocation data
   */
  private async fetchFundContentForType(
    fundType: FundType,
    startDate: string,
    endDate: string,
    fundCode?: string,
    throwOnNotFound: boolean = true
  ): Promise<ApiResponse<FundContentResponse>> {
    // If the requested range exceeds maxDaysPerRequest, split into chunks
    const totalDays = getDaysBetween(startDate, endDate);
    const MAX_DAYS = this.maxDaysPerRequest;

    let fundContent: FundContentResponse[] = [];
    if (totalDays > MAX_DAYS) {
      // Calculate all chunks
      const chunks: Array<{ start: string; end: string }> = [];
      let chunkStart = startDate;
      while (true) {
        const tentativeEnd = addDays(chunkStart, MAX_DAYS - 1);
        const chunkEnd = tentativeEnd > endDate ? endDate : tentativeEnd;
        chunks.push({ start: chunkStart, end: chunkEnd });
        if (chunkEnd === endDate) break;
        chunkStart = addDays(chunkEnd, 1);
      }

      console.log(`Fetching fund content [${fundType}] [fund: ${fundCode || 'all'}]: ${chunks.length} chunks (${totalDays} days total)`);

      // Execute chunks sequentially to avoid rate limiting
      for (let i = 0; i < chunks.length; i++) {
        const { start, end } = chunks[i];
        const chunkNumber = i + 1;
        const chunkName = `chunk ${chunkNumber}/${chunks.length} (${start} to ${end})`;

        console.log(`Fetching fund content [${fundType}] [fund: ${fundCode || 'all'}]: ${chunkName}`);

        const chunkContent = await this.retryWithDelay(
          async () => {
            const chunkRequest = this.buildAllocationRequest(fundType, start, end, fundCode);
            const chunkResponse = await this.baseClient.post<TefasAllocationApiResponse>(
              TEFAS_ALLOCATION_ENDPOINT,
              chunkRequest,
              { contentType: 'form' }
            );
            return this.processAllocationResponse(chunkResponse, fundType);
          },
          chunkName
        );

        fundContent.push(...chunkContent);
      }

      console.log(`Successfully fetched fund content [${fundType}] [fund: ${fundCode || 'all'}]: ${chunks.length}/${chunks.length} chunks (${totalDays} days)`);
    } else {
      // Single call path
      console.log(`Fetching fund content [${fundType}] [fund: ${fundCode || 'all'}]: single call (${startDate} to ${endDate})`);
      const allocationRequest = this.buildAllocationRequest(fundType, startDate, endDate, fundCode);
      const response = await this.baseClient.post<TefasAllocationApiResponse>(
        TEFAS_ALLOCATION_ENDPOINT,
        allocationRequest,
        { contentType: 'form' }
      );
      fundContent = this.processAllocationResponse(response, fundType);
      console.log(`Successfully fetched fund content [${fundType}] [fund: ${fundCode || 'all'}]: ${fundContent.length} records`);
    }

    // Ensure deterministic ordering by date ascending
    fundContent.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    // If specific fund code was requested, validate it exists
    if (fundCode && fundContent.length === 0 && throwOnNotFound) {
      throw new FundNotFoundError(fundCode);
    }

    return { results: fundContent };
  }

  /**
   * Fetches fund allocation data for all fund types and merges results.
   *
   * @private
   * @param startDate - Start date
   * @param endDate - End date
   * @param fundCode - Optional fund code
   * @returns Promise resolving to merged fund allocation data
   */
  private async fetchAllFundTypesContent(
    startDate: string,
    endDate: string,
    fundCode?: string
  ): Promise<ApiResponse<FundContentResponse>> {
    const allFundTypes = SUPPORTED_FUND_TYPES;
    const allResults: FundContentResponse[] = [];

    // Fetch data for each fund type sequentially to avoid rate limiting
    for (const fundType of allFundTypes) {
      try {
        const response = await this.fetchFundContentForType(fundType, startDate, endDate, fundCode, false);
        allResults.push(...response.results);
      } catch {
        // If a specific fund type fails, continue with others
        continue;
      }
    }

    // If specific fund code was requested, validate it exists in at least one fund type
    if (fundCode && allResults.length === 0) {
      throw new FundNotFoundError(`Fund '${fundCode}' not found in any fund type`);
    }

    return { results: allResults };
  }

  /**
   * Builds TEFAS API request parameters.
   *
   * @private
   * @param fundType - Fund type
   * @param startDate - Start date
   * @param endDate - End date
   * @param fundCode - Optional fund code
   * @returns TEFAS API request object
   */
  private buildTefasRequest(
    fundType: FundType,
    startDate: string,
    endDate: string,
    fundCode?: string
  ): TefasApiRequest {
    const request: TefasApiRequest = {
      fontip: fundType,
      bastarih: convertToTefasDateFormat(startDate),
      bittarih: convertToTefasDateFormat(endDate)
    };

    // Add fund code if provided
    if (fundCode) {
      request.fonkod = fundCode.trim().toUpperCase();
    }

    return request;
  }

  /**
   * Builds TEFAS allocation API request parameters.
   *
   * @private
   * @param fundType - Fund type
   * @param startDate - Start date
   * @param endDate - End date
   * @param fundCode - Optional fund code
   * @returns TEFAS allocation API request object
   */
  private buildAllocationRequest(
    fundType: FundType,
    startDate: string,
    endDate: string,
    fundCode?: string
  ): Record<string, string> {
    const request: Record<string, string> = {
      fontip: fundType,
      bastarih: convertToTefasDateFormat(startDate),
      bittarih: convertToTefasDateFormat(endDate)
    };

    // Add fund code if provided
    if (fundCode) {
      request.fonkod = fundCode.trim().toUpperCase();
    }

    return request;
  }

  /**
   * Processes TEFAS API response and maps it to our typed response.
   *
   * @private
   * @param response - Raw TEFAS API response
   * @param fundType - Fund type from the request
   * @returns Array of mapped fund history responses
   */
  private processTefasResponse(response: TefasApiResponse, fundType: FundType): FundHistoryResponse[] {
    return mapTefasResponse(response, fundType);
  }

  /**
   * Processes TEFAS allocation API response and maps it to our typed response.
   *
   * @private
   * @param response - Raw TEFAS allocation API response
   * @param fundType - Fund type from the request
   * @returns Array of mapped fund allocation responses
   */
  private processAllocationResponse(response: TefasAllocationApiResponse, fundType: FundType): FundContentResponse[] {
    return mapAllocationResponse(response, fundType);
  }

  /**
   * Maps Fundfy response to FundDetail shape
   */
  private mapFundfyResponse(fund: FundfyFund): FundDetail {
    return {
      code: fund.code,
      title: fund.title,
      auditFirm: fund.auditFirm ?? null,
      emailAddress: fund.emailAddress ?? null,
      fundDuration: fund.fundDuration ?? null,
      contactAddress: fund.contactAddress ?? null,
      founder: fund.founder ?? null,
      founderLogo: this.extractFilename(fund.founderLogo),
      umbrellaFundType: fund.umbrellaFundType ?? null,
      umbrellaFundName: fund.umbrellaFundName ?? null,
      fundCategory: fund.fundCategory
        ? {
            abbreviation: fund.fundCategory.abbreviation,
            fullName: fund.fundCategory.fullName
          }
        : null,
      risk: fund.risk ?? null,
      yearlyManagementFee: fund.yearlyManagementFee ?? null,
      buyingValor: fund.buyingValor ?? null,
      sellingValor: fund.sellingValor ?? null,
      strategyStatement: fund.strategyStatement ?? null,
      fundBenchmarks: fund.fundBenchmarks?.map(benchmark => ({
        benchmark: benchmark.benchmark,
        percentage: benchmark.percentage
      })) ?? []
    };
  }

  /**
   * Extracts filename from a URL string; returns null if input is falsy.
   */
  private extractFilename(url?: string | null): string | null {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const segments = parsed.pathname.split('/');
      return segments[segments.length - 1] || null;
    } catch {
      // If URL parsing fails, fall back to last path segment using string split
      const parts = url.split('/');
      return parts[parts.length - 1] || null;
    }
  }

  /**
   * Generates a unique cache key for getFund requests
   *
   * @private
   * @param startDate - Start date (normalized)
   * @param endDate - End date (normalized)
   * @param fundCode - Optional fund code
   * @param fundType - Optional fund type
   * @returns Cache key string
   */
  private generateCacheKey(
    startDate: string,
    endDate: string,
    fundCode?: string,
    fundType?: FundType
  ): string {
    const fundTypeKey = fundType || 'all';
    const fundCodeKey = fundCode || 'all';
    return `getFund:${fundTypeKey}:${startDate}:${endDate}:${fundCodeKey}`;
  }

  /**
   * Clears all cached fund data
   *
   * Use this method to force fresh data retrieval or to free up memory.
   * Subsequent getFund() calls will fetch data from the API.
   *
   * @example
   * ```typescript
   * const client = new TefasClient();
   * await client.getFund('2024-01-01', '2024-01-31'); // Fetches from API
   * await client.getFund('2024-01-01', '2024-01-31'); // Returns cached data
   * await client.clearCache();
   * await client.getFund('2024-01-01', '2024-01-31'); // Fetches from API again
   * ```
   */
  public async clearCache(): Promise<void> {
    await this.cache.clear();
  }

  /**
   * Invalidates cache for a specific fund/date range combination
   *
   * @param startDate - Start date in YYYY-MM-DD format or fuzzy date
   * @param endDate - End date in YYYY-MM-DD format or fuzzy date
   * @param fundCode - Optional specific fund code
   * @param fundType - Optional fund type
   *
   * @example
   * ```typescript
   * const client = new TefasClient();
   * // Invalidate specific cache entry
   * await client.invalidateCache('2024-01-01', '2024-01-31', 'TGE', FundType.EMK);
   * ```
   */
  public async invalidateCache(
    startDate: string,
    endDate: string,
    fundCode?: string,
    fundType?: FundType
  ): Promise<void> {
    const { startDate: normalizedStartDate, endDate: normalizedEndDate } =
      normalizeFuzzyDateRange(startDate, endDate);
    const cacheKey = this.generateCacheKey(
      normalizedStartDate,
      normalizedEndDate,
      fundCode,
      fundType
    );
    await this.cache.delete(cacheKey);
  }

  /**
   * Gets cache statistics
   *
   * Returns undefined if the cache adapter doesn't support statistics.
   *
   * @returns Cache statistics including size, TTL, and configuration, or undefined
   *
   * @example
   * ```typescript
   * const client = new TefasClient();
   * const stats = await client.getCacheStats();
   * if (stats) {
   *   console.log(`Cache has ${stats.size} entries, TTL: ${stats.ttl}ms`);
   * }
   * ```
   */
  public async getCacheStats(): Promise<CacheStats | undefined> {
    if (isCacheAdapterWithStats(this.cache)) {
      return await this.cache.getStats();
    }
    return undefined;
  }

  /**
   * Removes expired entries from the cache
   *
   * This method is useful for freeing up memory by removing stale entries.
   * The cache automatically checks expiration on access, but this method
   * allows for proactive cleanup.
   *
   * Returns undefined if the cache adapter doesn't support manual pruning.
   *
   * @returns Number of entries removed, or undefined if not supported
   *
   * @example
   * ```typescript
   * const client = new TefasClient();
   * const removed = await client.pruneCache();
   * if (removed !== undefined) {
   *   console.log(`Removed ${removed} expired cache entries`);
   * }
   * ```
   */
  public async pruneCache(): Promise<number | undefined> {
    if (isCacheAdapterWithStats(this.cache) && this.cache.prune) {
      return await this.cache.prune();
    }
    return undefined;
  }

  /**
   * Retries an async operation with random delays between attempts
   *
   * @private
   * @param operation - Async function to retry
   * @param operationName - Name of the operation for logging
   * @param maxRetries - Maximum number of retry attempts (default: 3)
   * @returns Result of the operation
   * @throws Last error if all retries fail
   */
  private async retryWithDelay<T>(
    operation: () => Promise<T>,
    operationName: string,
    maxRetries: number = 3
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        if (attempt < maxRetries) {
          // Random delay between 500ms and 1000ms
          const delay = Math.floor(Math.random() * 500) + 500;
          console.error(`ERROR: ${operationName} failed (attempt ${attempt}/${maxRetries}):`, lastError.message);
          console.log(`Retrying ${operationName} in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          // Final attempt failed
          console.error(`ERROR: ${operationName} failed after ${maxRetries} attempts:`, lastError.message);
        }
      }
    }

    throw lastError!;
  }

  /**
   * Translates errors to business-focused messages.
   *
   * @private
   * @param error - Error to translate
   * @returns Business-focused error
   */
  private translateError(error: unknown): Error {
    // If it's already a TEFAS error, return as-is
    if (error instanceof InvalidFundTypeError ||
        error instanceof InvalidDateError ||
        error instanceof DateRangeError ||
        error instanceof FundNotFoundError ||
        error instanceof TefasApiError ||
        error instanceof NetworkError ||
        error instanceof ConfigurationError ||
        error instanceof ValidationError) {
      return error;
    }

    // Handle network errors (check message first before HTTP status)
    if (error && typeof error === 'object' && 'message' in error) {
      const errorMessage = (error as Error).message.toLowerCase();
      if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('timeout')) {
        return new NetworkError('Unable to connect to TEFAS API. Please check your internet connection.');
      }
    }

    // Handle HTTP errors from base client
    if (error && typeof error === 'object' && 'status' in error) {
      const httpError = error as { status: number; statusText?: string };
      if (httpError.status === 404) {
        return new FundNotFoundError('Requested fund data not found');
      }
      if (httpError.status >= 500) {
        return new TefasApiError('TEFAS API is temporarily unavailable. Please try again later.');
      }
      if (httpError.status >= 400) {
        return new TefasApiError('Invalid request. Please check your parameters and try again.');
      }
    }

    // Default to generic TEFAS API error
    return new TefasApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      error
    );
  }
}

interface FundfyCategory {
  abbreviation: string;
  fullName: string;
}

interface FundfyBenchmark {
  benchmark: string;
  percentage: number;
}

interface FundfyFund {
  code: string;
  title: string;
  auditFirm?: string | null;
  emailAddress?: string | null;
  fundDuration?: string | null;
  contactAddress?: string | null;
  founder?: string | null;
  founderLogo?: string | null;
  umbrellaFundType?: string | null;
  umbrellaFundName?: string | null;
  fundCategory?: FundfyCategory | null;
  risk?: number | null;
  yearlyManagementFee?: number | null;
  buyingValor?: number | null;
  sellingValor?: number | null;
  strategyStatement?: string | null;
  fundBenchmarks?: FundfyBenchmark[] | null;
}

interface FundfyDetailResponse {
  fund?: FundfyFund | null;
}
